import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { api } from "../../api/apiClient";
import type { Location, SalesIvaSummary } from "../../api/types";
import { money, percentText } from "../../i18n/intl";
import { useAuth } from "../../state/AuthContext";
import { businessToday } from "../../utils/businessDay";
import { EmptyState } from "../ui/EmptyState";
import { Select } from "../ui/Select";
import { StatTile } from "../ui/StatTile";
import { MonthNav } from "./MonthNav";
import { quarterLabel, quarterMonthsText } from "./quarterText";

/**
 * G-171 — "IVA on sales" (owner; a manager for their own shop).
 *
 * IVA collected in a month = the IVA inside every counted sale line at the
 * rate frozen on it, less the refunds whose money moved in the month, per
 * line at the frozen rate. Split by rate, by the category frozen on the line
 * and, under "All shops", by shop; the quarter's IVA collected beside the
 * month (its months added, modelo 303). All arithmetic is the server's
 * (`finances/sales-iva.service.ts`): the card shows the figures it is sent.
 */
export function SalesIvaCard() {
  const { hasRole } = useAuth();
  const isOwner = hasRole("OWNER");
  const canSee = isOwner || hasRole("MANAGER");

  const today = useMemo(() => businessToday(), []);
  const [year, setYear] = useState(today.year);
  const [month, setMonth] = useState(today.month);
  const [locationId, setLocationId] = useState("");
  const [locations, setLocations] = useState<Location[]>([]);
  const [summary, setSummary] = useState<SalesIvaSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const shopSelectId = useId();

  useEffect(() => {
    if (!isOwner) return;
    void api
      .listLocations()
      .then((list) => setLocations(list.filter((l) => l.isActive)))
      .catch(() => setLocations([]));
  }, [isOwner]);

  const refresh = useCallback(async () => {
    if (!canSee) return;
    setLoading(true);
    setError(null);
    try {
      setSummary(await api.getSalesIvaSummary({ year, month, locationId: locationId || undefined }));
    } catch (err: unknown) {
      setSummary(null);
      setError(err instanceof Error ? err.message : "Could not load IVA on sales");
    } finally {
      setLoading(false);
    }
  }, [canSee, year, month, locationId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (!canSee) return null;

  const s = summary;
  const show = (value: string | null) => (loading ? "…" : money(value));
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

  return (
    <div className="card opcost-card purch-card">
      <div className="opcost-card-head">
        <div>
          <h3 className="opcost-card-title">IVA on sales</h3>
        </div>
      </div>

      <div className="opcost-controls">
        <MonthNav
          year={year}
          month={month}
          onChange={(y, m) => {
            setYear(y);
            setMonth(m);
          }}
        />
        {isOwner ? (
          <div className="field opcost-shop-field">
            <label htmlFor={shopSelectId}>Shop</label>
            <Select id={shopSelectId} value={locationId} onChange={(e) => setLocationId(e.target.value)}>
              <option value="">All shops</option>
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name} ({loc.code})
                </option>
              ))}
            </Select>
          </div>
        ) : null}
      </div>

      {error ? <div className="alert error">{error}</div> : null}

      <div className="stat-tile-row">
        <StatTile
          label="IVA collected"
          value={show(s?.collected.ivaEur ?? null)}
          hint={s ? `${money(s.collected.netEur)} before IVA · ${money(s.collected.grossEur)} with IVA` : undefined}
        />
        {s?.quarter ? (
          <StatTile
            label={`IVA collected · ${quarterLabel(s.quarter)}`}
            value={show(s.quarter.collected.ivaEur)}
            hint={quarterMonthsText(s.quarter)}
          />
        ) : null}
        <StatTile
          label="On sales"
          value={show(s?.sales.ivaEur ?? null)}
          hint={s ? `${plural(s.sales.count, "ticket", "tickets")} · ${money(s.sales.grossEur)} with IVA` : undefined}
        />
        <StatTile
          label="Refunds taken off"
          value={show(s?.refunds.ivaEur ?? null)}
          tone={s && Number(s.refunds.ivaEur) !== 0 ? "warning" : "neutral"}
          hint={s ? `${plural(s.refunds.count, "refund", "refunds")} this month · ${money(s.refunds.grossEur)} given back` : undefined}
        />
      </div>

      {s && s.byRate.length === 0 ? (
        <EmptyState title="No sales this month" message="No counted sale and no refund in this month." />
      ) : null}

      {s && s.byRate.length > 0 ? (
        <section className="purch-section">
          <h4 className="purch-section-title">By rate</h4>
          <div className="table-scroll" tabIndex={0} role="region" aria-label="IVA on sales by rate">
            <table className="table purch-table">
              <thead>
                <tr>
                  <th scope="col">Rate</th>
                  <th scope="col">Before IVA</th>
                  <th scope="col">IVA</th>
                  <th scope="col">With IVA</th>
                </tr>
              </thead>
              <tbody>
                {s.byRate.map((r) => (
                  <tr key={r.ratePercent}>
                    <th scope="row">{percentText(r.ratePercent)} %</th>
                    <td>{money(r.netEur)}</td>
                    <td>
                      <strong>{money(r.ivaEur)}</strong>
                    </td>
                    <td>{money(r.grossEur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {s && s.byCategory.length > 0 ? (
        <section className="purch-section">
          <h4 className="purch-section-title">By category</h4>
          <div className="table-scroll" tabIndex={0} role="region" aria-label="IVA on sales by category">
            <table className="table purch-table">
              <thead>
                <tr>
                  <th scope="col">Category</th>
                  <th scope="col">Rate</th>
                  <th scope="col">Before IVA</th>
                  <th scope="col">IVA</th>
                  <th scope="col">With IVA</th>
                </tr>
              </thead>
              <tbody>
                {s.byCategory.map((r) => (
                  <tr key={`${r.categoryId}-${r.ratePercent}`}>
                    <th scope="row">{r.label}</th>
                    <td>{percentText(r.ratePercent)} %</td>
                    <td>{money(r.netEur)}</td>
                    <td>
                      <strong>{money(r.ivaEur)}</strong>
                    </td>
                    <td>{money(r.grossEur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {s && s.byShop.length > 0 ? (
        <section className="purch-section">
          <h4 className="purch-section-title">By shop</h4>
          <div className="table-scroll" tabIndex={0} role="region" aria-label="IVA on sales by shop">
            <table className="table purch-table">
              <thead>
                <tr>
                  <th scope="col">Shop</th>
                  <th scope="col">Before IVA</th>
                  <th scope="col">IVA</th>
                  <th scope="col">With IVA</th>
                </tr>
              </thead>
              <tbody>
                {s.byShop.map((r) => (
                  <tr key={r.locationId}>
                    <th scope="row">{r.name}</th>
                    <td>{money(r.netEur)}</td>
                    <td>
                      <strong>{money(r.ivaEur)}</strong>
                    </td>
                    <td>{money(r.grossEur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
}
